import React, { useState } from 'react';
import { BarChart2Icon, PieChartIcon, TrendingUpIcon, CalendarIcon, DownloadIcon, FilterIcon, RefreshCwIcon, UserIcon, TagIcon, DollarSignIcon } from 'lucide-react';
export function ExpenseReports({
  locale = 'en-US'
}) {
  const [dateRange, setDateRange] = useState('last3months');
  const [groupBy, setGroupBy] = useState('category');
  const [reportType, setReportType] = useState('summary');
  // Format currency based on locale
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  // Sample data for reports
  const reportData = {
    totalExpenses: 45750.25,
    expensesByCategory: [{
      category: 'Travel',
      amount: 18250.75,
      percentage: 39.9
    }, {
      category: 'Training',
      amount: 8750.5,
      percentage: 19.1
    }, {
      category: 'Meals',
      amount: 5980.25,
      percentage: 13.1
    }, {
      category: 'Marketing',
      amount: 4520.5,
      percentage: 9.9
    }, {
      category: 'Software',
      amount: 3950.0,
      percentage: 8.6
    }, {
      category: 'Office',
      amount: 2850.75,
      percentage: 6.2
    }, {
      category: 'Other',
      amount: 1447.5,
      percentage: 3.2
    }],
    expensesByDepartment: [{
      department: 'Sales',
      amount: 15250.5,
      percentage: 33.3
    }, {
      department: 'Marketing',
      amount: 12750.75,
      percentage: 27.9
    }, {
      department: 'Engineering',
      amount: 8950.0,
      percentage: 19.6
    }, {
      department: 'Finance',
      amount: 4520.5,
      percentage: 9.9
    }, {
      department: 'HR',
      amount: 2450.25,
      percentage: 5.4
    }, {
      department: 'Operations',
      amount: 1828.25,
      percentage: 4.0
    }],
    monthlyTrend: [{
      month: 'Jun',
      amount: 8250.5
    }, {
      month: 'Jul',
      amount: 9750.25
    }, {
      month: 'Aug',
      amount: 12500.75
    }, {
      month: 'Sep',
      amount: 7850.5
    }, {
      month: 'Oct',
      amount: 7398.25
    }],
    topExpenseUsers: [{
      name: 'John Doe',
      amount: 7850.75,
      count: 12
    }, {
      name: 'Sarah Williams',
      amount: 6950.5,
      count: 8
    }, {
      name: 'Robert Brown',
      amount: 5750.25,
      count: 10
    }, {
      name: 'Jane Smith',
      amount: 4850.5,
      count: 7
    }, {
      name: 'Michael Johnson',
      amount: 3750.25,
      count: 6
    }]
  };
  const getDataByGrouping = () => {
    switch (groupBy) {
      case 'department':
        return reportData.expensesByDepartment;
      case 'user':
        return reportData.topExpenseUsers.map(user => ({
          label: user.name,
          amount: user.amount,
          percentage: user.amount / reportData.totalExpenses * 100
        }));
      case 'category':
      default:
        return reportData.expensesByCategory;
    }
  };
  const getGroupLabel = () => {
    switch (groupBy) {
      case 'department':
        return 'Department';
      case 'user':
        return 'User';
      case 'category':
      default:
        return 'Category';
    }
  };
  return <div data-id="element-6446">
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-6" data-id="element-6447">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-6448">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6449">
            Expense Reports
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6450">
            Generate and view expense reports by different criteria
          </p>
        </div>
        {/* Report controls */}
        <div className="p-4 border-b border-gray-200 sm:p-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-6451">
          <div data-id="element-6452">
            <label htmlFor="date-range" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6453">
              Date Range
            </label>
            <select id="date-range" value={dateRange} onChange={e => setDateRange(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-6454">
              <option value="last3months" data-id="element-6455">Last 3 Months</option>
              <option value="last6months" data-id="element-6456">Last 6 Months</option>
              <option value="lastyear" data-id="element-6457">Last Year</option>
              <option value="ytd" data-id="element-6458">Year to Date</option>
              <option value="custom" data-id="element-6459">Custom Range</option>
            </select>
          </div>
          <div data-id="element-6460">
            <label htmlFor="group-by" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6461">
              Group By
            </label>
            <select id="group-by" value={groupBy} onChange={e => setGroupBy(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-6462">
              <option value="category" data-id="element-6463">Category</option>
              <option value="department" data-id="element-6464">Department</option>
              <option value="user" data-id="element-6465">User</option>
            </select>
          </div>
          <div data-id="element-6466">
            <label htmlFor="report-type" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6467">
              Report Type
            </label>
            <select id="report-type" value={reportType} onChange={e => setReportType(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-6468">
              <option value="summary" data-id="element-6469">Summary</option>
              <option value="detailed" data-id="element-6470">Detailed</option>
              <option value="trend" data-id="element-6471">Trend Analysis</option>
            </select>
          </div>
          <div className="flex items-end" data-id="element-6472">
            <button className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-6473">
              <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-6474" />
              Generate Report
            </button>
          </div>
        </div>
        {/* Report summary */}
        <div className="p-6" data-id="element-6475">
          <div className="mb-6" data-id="element-6476">
            <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-6477">
              Expense Summary
            </h4>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3" data-id="element-6478">
              <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200" data-id="element-6479">
                <div className="p-5" data-id="element-6480">
                  <div className="flex items-center" data-id="element-6481">
                    <div className="flex-shrink-0" data-id="element-6482">
                      <DollarSignIcon className="h-6 w-6 text-indigo-600" data-id="element-6483" />
                    </div>
                    <div className="ml-5 w-0 flex-1" data-id="element-6484">
                      <dl data-id="element-6485">
                        <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-6486">
                          Total Expenses
                        </dt>
                        <dd data-id="element-6487">
                          <div className="text-lg font-medium text-gray-900" data-id="element-6488">
                            {formatCurrency(reportData.totalExpenses)}
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200" data-id="element-6489">
                <div className="p-5" data-id="element-6490">
                  <div className="flex items-center" data-id="element-6491">
                    <div className="flex-shrink-0" data-id="element-6492">
                      <TagIcon className="h-6 w-6 text-green-600" data-id="element-6493" />
                    </div>
                    <div className="ml-5 w-0 flex-1" data-id="element-6494">
                      <dl data-id="element-6495">
                        <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-6496">
                          Top {getGroupLabel()}
                        </dt>
                        <dd data-id="element-6497">
                          <div className="text-lg font-medium text-gray-900" data-id="element-6498">
                            {getDataByGrouping()[0].category || getDataByGrouping()[0].department || getDataByGrouping()[0].label}
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200" data-id="element-6499">
                <div className="p-5" data-id="element-6500">
                  <div className="flex items-center" data-id="element-6501">
                    <div className="flex-shrink-0" data-id="element-6502">
                      <UserIcon className="h-6 w-6 text-purple-600" data-id="element-6503" />
                    </div>
                    <div className="ml-5 w-0 flex-1" data-id="element-6504">
                      <dl data-id="element-6505">
                        <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-6506">
                          Top Spender
                        </dt>
                        <dd data-id="element-6507">
                          <div className="text-lg font-medium text-gray-900" data-id="element-6508">
                            {reportData.topExpenseUsers[0].name}
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Chart visualization */}
          <div className="mb-6" data-id="element-6509">
            <div className="flex justify-between items-center mb-4" data-id="element-6510">
              <h4 className="text-lg font-medium text-gray-900" data-id="element-6511">
                Expenses by {getGroupLabel()}
              </h4>
              <button className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:text-gray-800 active:bg-gray-50" data-id="element-6512">
                <DownloadIcon className="h-4 w-4 mr-1" data-id="element-6513" />
                Export
              </button>
            </div>
            {/* Simple bar chart visualization */}
            <div className="h-80 relative" data-id="element-6514">
              <div className="absolute inset-0 flex items-end" data-id="element-6515">
                {getDataByGrouping().map((item, index) => <div key={index} className="flex flex-col items-center justify-end mx-2 flex-1" data-id="element-6516">
                    <div className="w-full bg-indigo-500 rounded-t" style={{
                  height: `${item.amount / reportData.totalExpenses * 100 * 3}px`,
                  maxHeight: '90%'
                }} data-id="element-6517"></div>
                    <div className="mt-2 text-xs text-gray-500 truncate w-full text-center" data-id="element-6518">
                      {item.category || item.department || item.label}
                    </div>
                    <div className="text-xs font-medium" data-id="element-6519">
                      {formatCurrency(item.amount)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Monthly trend */}
          <div className="mb-6" data-id="element-6520">
            <div className="flex justify-between items-center mb-4" data-id="element-6521">
              <h4 className="text-lg font-medium text-gray-900" data-id="element-6522">
                Monthly Expense Trend
              </h4>
            </div>
            {/* Simple line chart visualization */}
            <div className="h-64 relative" data-id="element-6523">
              <div className="absolute inset-0 flex items-end" data-id="element-6524">
                {reportData.monthlyTrend.map((item, index) => <div key={index} className="flex flex-col items-center justify-end mx-2 flex-1" data-id="element-6525">
                    <div className="w-full bg-green-500 rounded-t" style={{
                  height: `${item.amount / 15000 * 100 * 2}px`,
                  maxHeight: '90%'
                }} data-id="element-6526"></div>
                    <div className="mt-2 text-xs text-gray-500" data-id="element-6527">
                      {item.month}
                    </div>
                    <div className="text-xs font-medium" data-id="element-6528">
                      {formatCurrency(item.amount)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Top expense users */}
          <div data-id="element-6529">
            <div className="flex justify-between items-center mb-4" data-id="element-6530">
              <h4 className="text-lg font-medium text-gray-900" data-id="element-6531">
                Top Expense Users
              </h4>
            </div>
            <div className="overflow-x-auto" data-id="element-6532">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-6533">
                <thead className="bg-gray-50" data-id="element-6534">
                  <tr data-id="element-6535">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6536">
                      User
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6537">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6538">
                      # of Claims
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6539">
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-6540">
                  {reportData.topExpenseUsers.map((user, index) => <tr key={index} data-id="element-6541">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-6542">
                        {user.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-6543">
                        {formatCurrency(user.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-6544">
                        {user.count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-6545">
                        {(user.amount / reportData.totalExpenses * 100).toFixed(1)}
                        %
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>;
}